/**
 * @file Helpers for managing client-side state related to post-photo-upload order confirmation.
 */

/**
 * Retrieves the list of order identifiers that have completed post-order flow from session storage.
 *
 * @returns {string[]} An array of order identifiers.
 */
export function getPostOrderFlowCompletedIds(): string[] {
  const key = `${window.location.pathname}:postOrderFlowCompleted`;
  const item = sessionStorage.getItem(key);

  if (item === null) {
    return [];
  }

  try {
    const parsed = JSON.parse(item);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Ignore parsing errors and return an empty array
    return [];
  }
}

/**
 * Checks session storageif the given order has completed post-order flow.
 *
 * @param {string} orderIdentifier - The order identifier to check.
 * @returns {boolean} True if the order identifier is confirmed, false otherwise.
 */
export function hasPostOrderFlowCompletedId(orderIdentifier: string): boolean {
  const postOrderFlowCompleted = getPostOrderFlowCompletedIds();
  return Array.isArray(postOrderFlowCompleted) && postOrderFlowCompleted.includes(orderIdentifier);
}

/**
 * Adds the given order identifier to the list of post-upload confirmed orders in session storage.
 *
 * @param {string} orderIdentifier - The order identifier to add.
 */
export function addPostOrderFlowCompletedId(orderIdentifier: string): void {
  if (!orderIdentifier) return;

  const postOrderFlowCompleted = getPostOrderFlowCompletedIds();

  if (Array.isArray(postOrderFlowCompleted) && !postOrderFlowCompleted.includes(orderIdentifier)) {
    const updatedConfirmed = [...postOrderFlowCompleted, orderIdentifier];
    sessionStorage.setItem(
      `${window.location.pathname}:postOrderFlowCompleted`,
      JSON.stringify(updatedConfirmed)
    );
  }
}
